<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <v-row>
            <v-col cols="4">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                v-model="searchCondition.site"
                :items="shopList"
                item-title="code_name"
                item-value="code"
                :label="$t('tanaoroshiInventoryDate.label.siteCode')"
                :clearable="true"
                return-object
              />
            </v-col>
            <v-col cols="4">
              <YearMonthPicker
                ref="inventoryYearMonth"
                v-model="searchCondition.inventoryDate"
                :label="$t('tanaoroshiInventoryDate.label.inventoryYearMonth')"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="text-right">
              <v-btn text @click="clear">{{ $t('common.action.clear') }}</v-btn>
              <v-btn
                theme="dark"
                class="ml-4 px-8 bg-grey-darken-3"
                elevation="2"
                @click.prevent="search"
                :loading="isSearching"
              >
                {{ $t('common.action.search') }}
              </v-btn>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" class="text-right">
              <input ref="uploadBtn" type="file" accept=".xlsx" style="display: none" @change="selectedFile" />
              <v-btn dark class="bg-indigo-darken-1" elevation="2" @click="onUploadBtnClick">
                {{ $t('tanaoroshiInventoryDate.action.upload') }}
              </v-btn>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-table density="compact" class="text-no-wrap">
                <thead>
                  <tr>
                    <th v-for="header in headers" :key="header">
                      {{ $t("tanaoroshiInventoryDate.label.table." + header) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(data, index) in dataList" :key="index">
                    <td v-for="header in headers" :key="header">{{ data[header] }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>
        </v-container>
        <ZeroData v-if="zeroDataWarningDialogFlg" :handle-ok="closeZeroDataDialog" />
        <MessageDialog
          v-if="commonMsgDlgVisible"
          :dialog-data="data4MsgDialog"
          :handle-ok="closeMessageDataDialog"
        />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import api from "@/apis/staff";
import utils from "@/utils";
import constant from "@/utils/constant";
import AppWrapperView from "@/components/views/AppWrapperView";
import MessageDialog from "@/components/dialog/MessageDialog";
import ZeroData from "@/components/dialog/ZeroDataDialog";
import YearMonthPicker from "@/components/YearMonthPicker";

export default {
  components: { AppWrapperView, MessageDialog, ZeroData, YearMonthPicker },
  computed: {
    headers() {
      return this.$getTableDef("TanaoroshiInventoryDatePage");
    },
    constant() {
      return constant;
    },
  },
  data: () => ({
    isSearching: false,
    shopList: [],
    dataList: [],
    searchCondition: {
      site: null,
      inventoryDate: "",
    },
    zeroDataWarningDialogFlg: false,
    commonMsgDlgVisible: false,
    data4MsgDialog: {
      message: "",
      errorList: [],
    },
  }),
  methods: {
    clear() {
      // 棚卸日をクリアし、画面表示と検索条件の不整合を防止
      this.$refs.inventoryYearMonth?.clear();
      this.searchCondition = {
        site: null,
        inventoryDate: "",
      };
      this.dataList = [];
    },
    closeZeroDataDialog() {
      this.zeroDataWarningDialogFlg = false;
    },
    closeMessageDataDialog() {
      this.commonMsgDlgVisible = false;
    },
    showMessage(message, errorList = []) {
      this.data4MsgDialog = {
        message: message,
        errorList: errorList,
      };
      this.commonMsgDlgVisible = true;
    },
    search() {
      this.isSearching = true;
      api.post("/tanaoroshi/inventory-date/search", {
        siteCode: this.searchCondition.site?.code ?? "",
        inventoryDate: this.searchCondition.inventoryDate ?? "",
      })
        .then((response) => {
          if (response.data.status == this.constant.RESPONSE_STATUS.SUCCESS.toString()) {
            this.dataList = response.data.results?.inventoryDateList ?? [];
            if (this.dataList.length == 0) {
              this.zeroDataWarningDialogFlg = true;
            }
          } else {
            this.dataList = [];
            this.showMessage(response.data.errorMessage ?? this.$t("common.message.error"), response.data.errorList ?? []);
          }
        })
        .catch((error) => {
          const message = error.response ? utils.getErrorMsg(error.response, this.$t) : this.$t("common.message.errorAndRetry");
          this.showMessage(message, error.response?.data?.errorList ?? []);
        })
        .finally(() => {
          this.isSearching = false;
        });
    },
    onUploadBtnClick() {
      this.$refs.uploadBtn.click();
    },
    selectedFile() {
      let formData = new FormData();
      formData.append("file", this.$refs.uploadBtn.files[0]);
      api.postFile("/tanaoroshi/inventory-date/upload", formData)
        .then((response) => {
          if (response.data.status == this.constant.RESPONSE_STATUS.SUCCESS.toString()) {
            // 過去日による登録スキップの店舗コード一覧
            const skippedPastDateSiteCodes = response.data.results?.skippedPastDateSiteCodes ?? "";
            if (skippedPastDateSiteCodes) {
              // 過去日を含む店舗がある場合は対象店舗を明示して警告表示
              this.showMessage(
                `${this.$t("tanaoroshiInventoryDate.message.successUpload")}\n`
                + `${this.$t("tanaoroshiInventoryDate.message.warningSkippedPastDateSiteCodes")}\n`
                + `${skippedPastDateSiteCodes}`
              );
            } else {
              // スキップ対象がない場合は通常の成功メッセージを表示
              this.showMessage(this.$t("tanaoroshiInventoryDate.message.successUpload"));
            }
            if (this.searchCondition.site?.code && this.searchCondition.inventoryDate) {
              this.search();
            }
          } else {
            this.showMessage(response.data.errorMessage ?? this.$t("common.message.error"), response.data.errorList ?? []);
          }
        })
        .catch((error) => {
          const message = error.response ? utils.getErrorMsg(error.response, this.$t) : this.$t("common.message.errorAndRetry");
          this.showMessage(message, error.response?.data?.errorList ?? []);
        })
        .finally(() => {
          this.$refs.uploadBtn.value = null;
        });
    },
  },
  mounted() {
    api.post("/store/search/all", {})
      .then((response) => {
        if (response.data.status != this.constant.RESPONSE_STATUS.SUCCESS.toString()) return;
        this.shopList = [];
        response.data.results?.shopList.forEach((element) => {
          this.shopList.push({ name: element.name, code: element.code, code_name: element.code + "_" + element.name });
        });
      })
      .catch(() => {
        this.shopList = [];
      });
  },
};
</script>
